import React, { useState, useMemo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, TextField } from '@material-ui/core';

import { Button } from '@/components/uikit';
import { MainLayout } from '@/components/layout';
import calendarImage from '@/assets/images/icon_calendar.svg';
import arrowLeft from '@/assets/images/arrow_left.png';
import {
  regexIsPhoneNumber,
  csChatBuilder,
  isPhoneNumberValid,
} from '@/utils/helper';
import { storeConsultation } from '@/store/consultationDuck';
import { RootState } from '@/store/rootReducer';
import { useTranslate } from '@/locale';

const HEADER_BOXSHADOW_VALUE = '0 6px 6px -8px rgba(0, 0, 0, 0.4)';
const FOOTER_BOXSHADOW_VALUE = '0 -6px 6px -8px rgba(0, 0, 0, 0.4)';

export default function SelectPatient() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { translate } = useTranslate();

  const consultationData = useSelector(
    (state: RootState) => state.consultation
  );
  const [name, setName] = useState<string>(consultationData.patient_name || '');
  const [phoneNumber, setPhoneNumber] = useState<string>(
    consultationData.patient_phone_number || ''
  );
  const [notes, setNotes] = useState<string>(consultationData.notes || '');

  const handleChangePhoneNumber = (value: string) => {
    if (!!value && !regexIsPhoneNumber(value)) return;
    setPhoneNumber(value);
  };

  const isValid = useMemo(() => {
    return Boolean((phoneNumber || '').length >= 8 && !!name);
  }, [name, phoneNumber]);

  const isDisabled = (value: boolean) =>
    Boolean(consultationData.patient_id && value);

  const handleSubmit = () => {
    dispatch(
      storeConsultation({
        patient_name: name,
        patient_phone_number: phoneNumber,
        notes,
      })
    );
    const data = {
      patient_name: name,
      patient_phone_number: phoneNumber,
      notes,
      doctor_name: consultationData.doctor?.doctor_name || '',
      consultation_date: moment(consultationData?.consultation_date).format(
        'DD MMMM YYYY'
      ),
      consultation_time: (consultationData?.consultation_time || []).join('/'),
    };

    const text = `Halo Lifepack!\n\nNama: ${data.patient_name}\nNomor HP: ${data.patient_phone_number}\nUmur: \nTanggal lahir: \nJenis kelamin: (L/P)\nKeluhan: ${data.notes}\nSaya ingin berkonsultasi dengan ${data.doctor_name} untuk tanggal ${data.consultation_date} jam ${data.consultation_time}.`;

    window.location.href = csChatBuilder(encodeURIComponent(text));
  };

  const handleBack = () => {
    if (location.key) {
      history.goBack();
    } else {
      window.location.href = 'https://lifepack.id/konsultasi-psikiater/';
    }
  };

  const onBack = () => {
    dispatch(
      storeConsultation({
        patient_name: name,
        patient_phone_number: phoneNumber,
        notes,
      })
    );
    handleBack();
  };

  useEffect(() => {
    if (
      !consultationData?.consultation_date ||
      !consultationData?.consultation_time?.length ||
      !consultationData?.doctor?.doctor_id
    ) {
      handleBack();
    }
  }, []);

  return (
    <MainLayout
      hideBanner
      style={{
        backgroundColor: 'white',
        padding: '0',
      }}
    >
      {/* ANCHOR - Header */}
      <Box
        textAlign="center"
        boxShadow={HEADER_BOXSHADOW_VALUE}
        p="16px"
        display="flex"
        justifyContent="space-between"
        position="relative"
      >
        <div onClick={() => onBack()} style={{ cursor: 'pointer' }}>
          <img width="12px" src={arrowLeft} alt="arrow_left" />
        </div>
        <h2 style={{ fontSize: 18, margin: 0 }}>
          {translate('patientInformation')}
        </h2>
        <div />
      </Box>
      {/* ANCHOR - Selected Schedule */}
      <Box
        style={{
          backgroundColor: '#F3F7FF',
        }}
      >
        <Box
          display="flex"
          flex={1}
          flexDirection="row"
          fontSize="14px"
          fontWeight={600}
          padding="4px 16px"
        >
          <img src={calendarImage} className="mr-14" alt="Tanggal" />
          <p>
            {moment(consultationData.consultation_date).format(
              'dddd, DD MMMM YYYY'
            )}
          </p>
          <p className="mr-14 ml-14">|</p>
          <p>
            {(consultationData.consultation_time || [])
              .join(', ')
              .replaceAll(':', '.')}
          </p>
        </Box>
      </Box>
      {/* ANCHOR - Form */}
      <Box padding="0 16px">
        <p
          className="MuiFormLabel-root"
          style={{ marginBottom: '6px', fontSize: '14px', color: '#363655CC' }}
        >
          {translate('patient')}
        </p>
        <TextField
          variant="outlined"
          fullWidth
          InputProps={{
            style: {
              borderRadius: '10px',
              backgroundColor: isDisabled(
                Boolean(consultationData?.patient_name)
              )
                ? '#F4F5F5'
                : '',
            },
          }}
          disabled={isDisabled(Boolean(consultationData?.patient_name))}
          placeholder={translate('inputPatientName')}
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <p
          className="MuiFormLabel-root"
          style={{ marginBottom: '6px', fontSize: '14px', color: '#363655CC' }}
        >
          {translate('phoneNumber')}
        </p>
        <TextField
          variant="outlined"
          fullWidth
          InputProps={{
            style: {
              borderRadius: '10px',
              backgroundColor: isDisabled(
                isPhoneNumberValid(consultationData.patient_phone_number ?? '')
              )
                ? '#F4F5F5'
                : '',
            },
          }}
          type="tel"
          disabled={isDisabled(
            isPhoneNumberValid(consultationData.patient_phone_number ?? '')
          )}
          placeholder="+6281234567890"
          value={phoneNumber}
          onChange={e => handleChangePhoneNumber(e.target.value)}
        />
        <p
          className="MuiFormLabel-root"
          style={{ marginBottom: '6px', fontSize: '14px', color: '#363655CC' }}
        >
          {translate('symptomAndCondition')}
        </p>
        <TextField
          variant="outlined"
          fullWidth
          InputProps={{
            style: {
              borderRadius: '10px',
            },
          }}
          multiline
          minRows={4}
          maxRows={4}
          placeholder={translate('tellUsWhatYouFeel')}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </Box>
      {/* ANCHOR - Footer */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        display="flex"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          maxWidth="600px"
          width="100%"
          px={4}
          py={2}
          display="flex"
          justifyContent="space-between"
          bgcolor="white"
          boxShadow={FOOTER_BOXSHADOW_VALUE}
        >
          <Button
            borderRadius="100%"
            size="large"
            style={{
              flex: 1,
              width: 'auto',
              margin: 0,
              borderRadius: '24px',
              padding: '12px 24px',
            }}
            onClick={handleSubmit}
            disabled={!isValid}
          >
            {translate('scheduleConsultation')}
          </Button>
        </Box>
      </Box>
    </MainLayout>
  );
}
